import { AutoTunePresentationSchema } from "@/pages/auto-tune/data/presentation";
import { z } from "zod";

// =====================================
// Databricks Auto-Tune Recommendations (Auto Tune tab)
// =====================================
export const DbxAutoRecommendationItemSchema = z.object({
  presentation: AutoTunePresentationSchema.optional(),
  applied_presentation: AutoTunePresentationSchema.optional(),
  task_status: z.enum(["applied", "pending_apply", "recommended", "no_change"]).optional(),
  change_description: z.string().optional(),
  entity_id: z.string().nullish(),
  entity_name: z.string().nullish(),
  entity_type: z.string().nullish(),
  // Empty string for job-level recs; non-empty for per-task recs.
  task_key: z.string().nullish(),
  owned_by: z.string().nullish(),
  // Worker/driver split landed in altimate-backend #5050 + #5137; the
  // BE no longer surfaces the legacy single-instance fields.
  current_worker_node_type: z.string().nullish(),
  current_driver_node_type: z.string().nullish(),
  current_min_workers: z.number().nullish(),
  current_max_workers: z.number().nullish(),
  current_cost_per_hr: z.number().nullish(),
  // recommended_* come from the rec table's recommendation_metadata
  // JSONB and are flattened by the BE @property accessors before
  // serialisation.
  recommended_worker_instance: z.string().nullish(),
  recommended_driver_instance: z.string().nullish(),
  recommended_min_workers: z.number().nullish(),
  recommended_max_workers: z.number().nullish(),
  recommended_cost_per_hr: z.number().nullish(),
  estimated_savings_pct: z.number().nullish(),
  efficiency_score: z.number().nullish(),
  // Engineer prose from the producer. Never shown to customers.
  reasoning: z.string().nullish(),
  // Customer-facing explanation, non-null only for rows the producer marked
  // customer-facing. Absent on backends that predate the field.
  customer_reasoning: z.string().nullish(),
  model_confidence: z.number().nullish(),
  model_profile: z.string().nullish(),
  // Inference window (replaces metrics_*_date in the v2.4 schema).
  inference_start_date: z.string().nullish(),
  inference_end_date: z.string().nullish(),
  instance_id: z.number(),
  updated_on: z.string().nullish(),
  // Server-computed projected monthly $ savings, or null when missing.
  monthly_savings_usd: z.number().nullish(),
  // Lower bound of the autoscaling cost range, equal to the cost field
  // when min == max. Lets the card show "$X.XX–Y.YY/hr".
  current_cost_per_hr_low: z.number().nullish(),
  recommended_cost_per_hr_low: z.number().nullish(),
  // Server-computed floor of the savings range
  // (``monthly_savings_usd`` is the ceiling). Displayed as-is — the FE
  // never derives its own band.
  monthly_savings_min_usd: z.number().nullish(),
  // Monthly $ savings from DBU consumption only (excludes infra).
  // Used as the apples-to-apples numerator against the job's DBU-only
  // ``Est. Cost`` for the "% of job cost" hint; the dollar headline
  // still uses ``monthly_savings_min_usd`` / ``monthly_savings_usd`` so the user sees
  // total realizable savings, not just the DBU portion.
  monthly_dbu_savings_usd: z.number().nullish(),
  // LEFT-JOIN'd from databricks_auto_tune_configuration. Null when no
  // config row yet (treat as disabled).
  auto_tune_mode: z.string().nullish(),
  // Upstream task_keys this rec depends on, used by the Tasks DAG view
  // to draw edges. Defaults to `[]` when the BE prereq is missing so the
  // FE renders a node-only DAG instead of crashing.
  depends_on: z.array(z.string()).default([]),
  // Latest audit event type for this rec (APPLIED / FAILED / etc.) —
  // drives the DAG node status colour. `null` when no audit row exists
  // yet for this task.
  latest_action_event_type: z.string().nullish(),
});

export const DbxAutoRecommendationsResponseSchema = z.object({
  recommendations: z.array(DbxAutoRecommendationItemSchema),
  total_count: z.number().default(0),
});

export type DbxAutoRecommendationItem = z.infer<typeof DbxAutoRecommendationItemSchema>;
export type DbxAutoRecommendationsResponse = z.infer<typeof DbxAutoRecommendationsResponseSchema>;

// =====================================
// Auto-Tune Toggle (PATCH)
// =====================================
export const ToggleAutoTunePreflightSchema = z.object({
  blocked: z.boolean().default(false),
  reason: z.string().nullish(),
  actionable_message: z.string().nullish(),
  turn_off_allowed: z.boolean().nullish(),
});

// Sync rollback context (v2.5 §15). When the user disables an APPLIED
// rec, the BE checks for active runs first. If any exist and the call
// didn't pass force=true, the BE short-circuits with
// needs_confirmation=true and the FE renders an interstitial.
export const RollbackInfoSchema = z.object({
  performed: z.boolean().default(false),
  needs_confirmation: z.boolean().default(false),
  active_run_ids: z.array(z.number()).nullish(),
  reason: z.string().nullish(),
});

export const ToggleAutoTuneResponseSchema = z.object({
  auto_tune_mode: z.string().nullish(),
  audit_event_id: z.number().nullish(),
  entity_name: z.string().nullish(),
  preflight: ToggleAutoTunePreflightSchema,
  rollback: RollbackInfoSchema.optional().default({
    performed: false,
    needs_confirmation: false,
  }),
});

export type ToggleAutoTunePreflight = z.infer<typeof ToggleAutoTunePreflightSchema>;
export type RollbackInfo = z.infer<typeof RollbackInfoSchema>;
export type ToggleAutoTuneResponse = z.infer<typeof ToggleAutoTuneResponseSchema>;
